import { addMonths, endOfMonth, isFirstDayOfMonth, startOfDay, startOfMonth } from "date-fns";
import { AuditDevice } from "../models/AuditDevice";
import { AuditTrip, DetectedEvent, RefuelEntry, RefuelSource } from "../models/AuditTrip";
import { detectedAmount } from "./detectedEvents";
import { DayConsumptionLookup, measuredConsumption } from "./tripConsumption";
import { FUEL_TYPE_GROUPS, FuelType } from "@/constants/FuelTypeGroups";
import { REASON_TAX_CLASSES, ReasonTaxClass } from "@/constants/ReasonTaxClasses";

/** Kilometres of every Geotab trip of a vehicle on one calendar day of the period. */
export interface VehicleDay {
  deviceId: string;
  dateKey: string;
  km: number;
}

export type TaxClassFigures = Record<ReasonTaxClass, number>;

/** Where the summary's refuelled amount comes from; "detected" only stands in without a transaction. */
export type SummaryRefuelSource = RefuelSource | "detected";

/** Which figure the consumption split divides by the class kilometres. */
export type SplitBasis = "consumption" | "refuel";

export interface ConsumptionSplit {
  basis: SplitBasis;
  /** The basis figure divided in the ratio of the class kilometres (rule of three). */
  figures: TaxClassFigures;
}

export interface VehiclePeriodSummary {
  device: AuditDevice;
  /** Kilometres of the entries per tax class; unbooked trips count as "none". */
  km: TaxClassFigures;
  /** Kilometres of every Geotab trip of the vehicle in the period, booked or not (reference). */
  geotabKm: number;
  /** Sum of the whole-day consumption over the days with trips; undefined without a drive type group or data. */
  consumption?: number;
  /**
   * Litres of every fuel transaction in the period without AdBlue; without a transaction, Geotab's
   * detected fill-ups (litres) or charging sessions (kWh) of the period; undefined when there is neither.
   */
  refuelLiters?: number;
  /** Amount per origin behind `refuelLiters`, for the tooltip. */
  refuelLitersBySource: Record<SummaryRefuelSource, number>;
  /**
   * Consumption per tax class: `consumption` (or without one `refuelLiters`) divided in the ratio
   * of the class kilometres; undefined without a basis or without class kilometres.
   */
  split?: ConsumptionSplit;
}

/** Vehicles without a drive type group are summed under this key (kilometres and fill-ups only). */
export type SummaryUnitKey = FuelType | "unknown";

export interface PeriodTotals {
  km: TaxClassFigures;
  geotabKm: number;
  /** Consumption figures cannot be added across units, so they are summed per drive type. */
  byUnit: Partial<Record<SummaryUnitKey, { consumption?: number; refuelLiters?: number; split?: TaxClassFigures }>>;
}

export interface PeriodSummary {
  vehicles: VehiclePeriodSummary[];
  totals: PeriodTotals;
}

export type UnitTotals = NonNullable<PeriodTotals["byUnit"][SummaryUnitKey]>;

const UNIT_ORDER: SummaryUnitKey[] = [...FUEL_TYPE_GROUPS.map(group => group.fuelType), "unknown"];

/** The total buckets in a stable order: drive types first, vehicles without a group last. */
export function orderedUnitTotals(totals: PeriodTotals): [SummaryUnitKey, UnitTotals][] {
  return UNIT_ORDER.flatMap(unit => {
    const bucket = totals.byUnit[unit];
    return bucket ? [[unit, bucket] as [SummaryUnitKey, UnitTotals]] : [];
  });
}

interface Args {
  /** Entries of both views after the reason filter. */
  trips: AuditTrip[];
  vehicleDays: VehicleDay[];
  dayConsumption: DayConsumptionLookup;
  /** Every fuel transaction of the period per vehicle. */
  refuels: Map<string, RefuelEntry[]>;
  /** Geotab's detected fill-ups and charging sessions of the period per vehicle. */
  detected: Map<string, DetectedEvent[]>;
  devices: Map<string, AuditDevice>;
}

const zeroFigures = (): TaxClassFigures => ({ favored: 0, notFavored: 0, none: 0 });

/** Kilometres of every booked entry, the denominator of the consumption split. */
export function classKmTotal(km: TaxClassFigures): number {
  return REASON_TAX_CLASSES.reduce((sum, taxClass) => sum + km[taxClass], 0);
}

const addOptional = (total: number | undefined, value: number | undefined) =>
  value == null ? total : (total ?? 0) + value;

/**
 * The basis figure split in the ratio of the class kilometres. Without a basis or without class
 * kilometres there is nothing to divide.
 */
function splitConsumption(summary: Pick<VehiclePeriodSummary, "km" | "consumption" | "refuelLiters">): ConsumptionSplit | undefined {
  const basis: SplitBasis | undefined = summary.consumption != null ? "consumption" : summary.refuelLiters != null ? "refuel" : undefined;
  if (!basis) return undefined;
  const base = basis === "consumption" ? summary.consumption! : summary.refuelLiters!;
  const classKm = classKmTotal(summary.km);
  if (classKm <= 0) return undefined;
  const figures = zeroFigures();
  for (const taxClass of REASON_TAX_CLASSES) figures[taxClass] = (base * summary.km[taxClass]) / classKm;
  return { basis, figures };
}

/**
 * The consumption split is only shown over at least one full calendar month (1st to last day),
 * so tank levels at the period's edges do not distort the ratio. Picked days are local-midnight
 * dates.
 */
export function containsFullCalendarMonth(fromDay: Date, toDay: Date): boolean {
  const firstMonth = isFirstDayOfMonth(fromDay) ? startOfMonth(fromDay) : startOfMonth(addMonths(fromDay, 1));
  return startOfDay(endOfMonth(firstMonth)).getTime() <= startOfDay(toDay).getTime();
}

/**
 * One line per vehicle over the audited period: kilometres per tax class from the listed
 * entries, Geotab's kilometres of every trip as reference, the litres or kWh the vehicle used
 * (sum of the whole-day figures, days without a measurement left out), the amount filled up and
 * the consumption split per tax class. A vehicle appears when it has entries or Geotab trips in
 * the period.
 */
export function buildPeriodSummary({ trips, vehicleDays, dayConsumption, refuels, detected, devices }: Args): PeriodSummary {
  const byDevice = new Map<string, VehiclePeriodSummary>();
  const summaryOf = (device: AuditDevice) => {
    let summary = byDevice.get(device.id);
    if (!summary) {
      summary = {
        device,
        km: zeroFigures(),
        geotabKm: 0,
        refuelLitersBySource: { app: 0, import: 0, detected: 0 },
      };
      byDevice.set(device.id, summary);
    }
    return summary;
  };

  // Unbooked trips carry no reason, so their kilometres land in "unassigned": the split's
  // denominator is every kilometre the vehicle drove, as in Geotab's own figures.
  for (const trip of trips) {
    const summary = summaryOf(trip.device);
    if (trip.distance != null && Number.isFinite(trip.distance)) summary.km[trip.taxClass] += trip.distance;
  }

  for (const day of vehicleDays) {
    const device = devices.get(day.deviceId);
    if (!device) continue;
    const summary = summaryOf(device);
    summary.geotabKm += day.km;
    // 0 over driven kilometres is a missing signal, the same rule as for every cell of the table.
    summary.consumption = addOptional(summary.consumption, measuredConsumption(dayConsumption(day.deviceId, day.dateKey), day.km));
  }

  for (const [deviceId, entries] of refuels) {
    const device = devices.get(deviceId);
    if (!device || !byDevice.has(deviceId)) continue;
    const summary = summaryOf(device);
    for (const entry of entries) {
      if (entry.kind !== "fuel") continue;
      summary.refuelLiters = (summary.refuelLiters ?? 0) + entry.liters;
      summary.refuelLitersBySource[entry.source] += entry.liters;
    }
  }

  // Geotab's own detection stands in only for a vehicle without any transaction in the period.
  for (const [deviceId, events] of detected) {
    const summary = byDevice.get(deviceId);
    if (!summary || summary.refuelLiters != null) continue;
    const amount = detectedAmount(events);
    if (amount == null) continue;
    summary.refuelLiters = amount;
    summary.refuelLitersBySource.detected = amount;
  }

  const vehicles = [...byDevice.values()].sort((a, b) => a.device.name.localeCompare(b.device.name, "de"));
  const totals: PeriodTotals = { km: zeroFigures(), geotabKm: 0, byUnit: {} };
  for (const summary of vehicles) {
    summary.split = splitConsumption(summary);
    for (const taxClass of REASON_TAX_CLASSES) totals.km[taxClass] += summary.km[taxClass];
    totals.geotabKm += summary.geotabKm;
    const unit: SummaryUnitKey = summary.device.fuelType ?? "unknown";
    const bucket = (totals.byUnit[unit] ??= {});
    bucket.consumption = addOptional(bucket.consumption, summary.consumption);
    bucket.refuelLiters = addOptional(bucket.refuelLiters, summary.refuelLiters);
    if (summary.split) {
      bucket.split ??= zeroFigures();
      for (const taxClass of REASON_TAX_CLASSES) bucket.split[taxClass] += summary.split.figures[taxClass];
    }
  }
  return { vehicles, totals };
}
